import { useOrganizationContext } from "@/orgs/useOrganizationContext";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ReactNode, useContext, useEffect } from "react";
import {
  AuthContext,
  AuthProvider,
  IAuthContext,
} from "react-oauth2-code-pkce";
import { getOrganizationSelectionUrl, UserDetails } from ".";
import { ExternalRedirect } from "../components/ExternalRedirect";
import { useUserPlatformPermissionsQuery } from "../hooks/useUserPlatformPermissionsQuery";
import portalApiClient, {
  genericPortalApiClient,
  getCurrentUserDetails,
} from "../portal-api";
import sessionManagementApiClient from "../session-management-api";
import { AuthenticationContext } from "./AuthenticationContext";
import { useAuthConfig } from "./useAuthConfig";

export function AuthenticationProvider({ children }: { children: ReactNode }) {
  const { error, token, logIn, loginInProgress }: IAuthContext =
    useContext(AuthContext);
  const organization = useOrganizationContext();

  useEffect(() => {
    if (!token) return;

    // ensure the token is sent with requests to portal and sm api
    [
      portalApiClient,
      genericPortalApiClient,
      sessionManagementApiClient,
    ].forEach((apiClient) => {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    });

    // handle 401s
    const myInterceptor = portalApiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we're unauthorized, user must login again (but only do this if not yet refreshing)
        if (error?.response?.status === 401) {
          logIn();
        }
        throw error;
      },
    );

    return () => {
      portalApiClient.interceptors.response.eject(myInterceptor);
    };
  }, [token, logIn]);

  // send organization id with requests to session management api
  useEffect(() => {
    sessionManagementApiClient.defaults.headers.common[
      "Portal-Organization-Id"
    ] = organization.id;
  }, [organization.id]);

  // make sure organization-speicifc requests go to the right domain
  useEffect(() => {
    // In dev, backend calls go through the Vite proxy on the SPA's own
    // origin — rewriting the hostname here would bypass that and break
    // cookie-dependent flows.
    if (import.meta.env.DEV) return;
    if (!portalApiClient.defaults.baseURL) {
      return;
    }

    const currentBaseUrl = new URL(portalApiClient.defaults.baseURL); // throws if baseURL is not a valid URL
    currentBaseUrl.hostname = organization.domain;
    portalApiClient.defaults.baseURL = currentBaseUrl.toString();
  }, [organization.domain]);

  const {
    data: user,
    error: loadUserDetailsError,
    isLoading: isPendingAuthentication,
    isSuccess: isUserSuccess,
  } = useQuery<UserDetails, AxiosError>({
    queryKey: ["current-user"],
    queryFn: getCurrentUserDetails,
    enabled: !!token,
    retry: false,
  });

  const { data: userPlatformPermissions } = useUserPlatformPermissionsQuery(
    user?.id,
    organization.id,
    { enabled: !!user },
  );
  const isUserPartOfOrganization =
    user &&
    organization &&
    user.organizations.find((org) => org.id === organization.id);

  if (isUserSuccess && !isUserPartOfOrganization) {
    // the user is not part of the active organization, go to backend to select one the user is actually part of
    return (
      <ExternalRedirect
        url={getOrganizationSelectionUrl(window.location.pathname)}
      />
    );
  }

  // if user details could not be fetched due to 401, redirect to login
  if (loadUserDetailsError && loadUserDetailsError.response?.status === 401) {
    // trigger re-login
    logIn();
    return null;
  }

  return (
    <AuthenticationContext
      value={{
        token,
        error: loadUserDetailsError?.message ?? error ?? undefined,
        user,
        user_permissions: userPlatformPermissions ?? [],
        isPendingAuthentication: isPendingAuthentication || loginInProgress,
      }}
    >
      {children}
    </AuthenticationContext>
  );
}

export function AuthenticationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <AuthProvider authConfig={useAuthConfig()}>
      <AuthenticationProvider>{children}</AuthenticationProvider>
    </AuthProvider>
  );
}
